package nl.staartvin.solarpower.data.rivm

import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.get
import io.ktor.client.request.parameter
import kotlinx.datetime.Clock
import kotlinx.datetime.Instant
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.LocalTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toInstant
import kotlinx.datetime.toLocalDateTime
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.doubleOrNull
import kotlinx.serialization.json.jsonPrimitive
import nl.staartvin.solarpower.data.model.Location
import nl.staartvin.solarpower.data.model.UvDay
import nl.staartvin.solarpower.data.model.UvPoint
import nl.staartvin.solarpower.data.model.UvSource
import kotlin.time.Duration.Companion.minutes

/**
 * RIVM Zonkracht WMS data source — Netherlands only.
 *
 * Hits the GetFeatureInfo endpoint with a tiny bbox around the target lat/lon
 * and pulls 147 "BandN" properties out of the GeoJSON response. The band layout
 * (inferred empirically; no `<Dimension>` exposed by RIVM):
 *
 *   Band 1       : latest measured UVI (= last non-(-1) measured band)
 *   Band 2       : unknown, always 0
 *   Band 3       : daily UV dose so far today (J/m² erythemal)
 *   Bands 4..75  : forecast UVI in 15-minute steps, starting at 04:00 UTC
 *   Bands 76..80 : padding (-1)
 *   Bands 81..   : measured UVI in 15-minute steps, starting at 04:00 UTC,
 *                  truncating roughly at "now" (with ~30 min satellite-processing lag)
 *
 * BBOX gotcha — WMS 1.3.0 + EPSG:4326 is **LAT-FIRST**:
 *   BBOX = minLat,minLon,maxLat,maxLon  (NOT minLon,minLat,maxLon,maxLat).
 * This is the inverse of WMS 1.1.1 and most other geographic APIs. Easy to
 * get backwards. Write a unit test if you ever refactor this.
 */
class RivmWmsClient(
    private val http: HttpClient,
    // Overridable so the web build can point at a same-origin proxy path
    // (browser CORS) instead of the upstream directly. Native apps use the
    // default.
    private val baseUrl: String = BASE_URL,
) {

    suspend fun fetch(location: Location): UvDay {
        val response: RivmResponse = http.get(baseUrl) {
            parameter("SERVICE", "WMS")
            parameter("VERSION", "1.3.0")
            parameter("REQUEST", "GetFeatureInfo")
            parameter("QUERY_LAYERS", "rivm_zonkracht")
            parameter("LAYERS", "rivm_zonkracht")
            parameter("INFO_FORMAT", "application/json")
            parameter("FEATURE_COUNT", "1")
            parameter("CRS", "EPSG:4326")
            parameter("WIDTH", "100")
            parameter("HEIGHT", "100")
            parameter("I", "50")
            parameter("J", "50")
            parameter("BBOX", bboxLatFirst(location, delta = 0.001))
        }.body()

        val bands = response.features.firstOrNull()?.properties
            ?: error("RIVM response had no features")

        // Band-to-time alignment (verified empirically against RIVM's own website):
        //   Forecast bands 4..75  : start at 03:00 UTC, 15-min steps
        //   Measured bands 81..N  : start at 04:15 UTC, 15-min steps
        //                          (= 75 min after forecast start)
        //
        // The handoff doc guessed "starts ~04:00 UTC" for both; the truth is
        // they're staggered. Diagnosis: every measured band's value precisely
        // matches a forecast band 72 indices earlier. Cross-checking that
        // against the actual wall-clock time of the latest measurement pins
        // forecast start to 03:00 UTC and measured start to 04:15 UTC.
        //
        // Note: these constants are observed in late June and may shift with
        // the season — RIVM might tie the start to sunrise. Worth re-verifying
        // by inspecting the wire data on a winter day if curves look misaligned.
        val todayUtc = Clock.System.now().toLocalDateTime(TimeZone.UTC).date
        val forecastStart = LocalDateTime(todayUtc, LocalTime(3, 0)).toInstant(TimeZone.UTC)
        val measuredStart = LocalDateTime(todayUtc, LocalTime(4, 15)).toInstant(TimeZone.UTC)

        val forecast = (4..75).mapNotNull { sample(bands, it, forecastStart, firstBand = 4) }
        val measured = (81..147).mapNotNull { sample(bands, it, measuredStart, firstBand = 81) }

        // "Now" = the most recent satellite reading (RIVM's "Laatst gemeten").
        // ~0-30 min behind the wall clock due to processing lag — that's a
        // feature, not a bug: it's a real measurement, not a prediction. Falls
        // back to (deviceTime, 0.0) before sunrise when no measurements exist.
        val now = measured.lastOrNull() ?: UvPoint(Clock.System.now(), 0.0)

        return UvDay(
            location = location,
            now = now,
            // `now` already appears as the last entry of [measured]; prepend
            // it to [forecast] so the curves visually connect at that point.
            forecast = listOf(now) + forecast,
            measured = measured,
            source = UvSource.RIVM,
        )
    }

    /**
     * Reads one band as a [UvPoint], or null if the band is missing or marked
     * as -1 (RIVM's sentinel for "no data — padding or future-measured").
     */
    private fun sample(
        bands: JsonObject,
        bandNumber: Int,
        startTime: Instant,
        firstBand: Int,
    ): UvPoint? {
        val raw = bands["Band$bandNumber"]?.jsonPrimitive?.doubleOrNull ?: return null
        if (raw < 0) return null
        val time = startTime.plus(((bandNumber - firstBand) * 15).minutes)
        return UvPoint(time, raw)
    }

    /**
     * Builds the WMS 1.3.0 EPSG:4326 BBOX string — LAT FIRST.
     * Locale-independent: `Double.toString()` always uses `.` as separator.
     */
    private fun bboxLatFirst(loc: Location, delta: Double): String =
        "${loc.latitude - delta},${loc.longitude - delta}," +
            "${loc.latitude + delta},${loc.longitude + delta}"

    companion object {
        const val BASE_URL = "https://data.rivm.nl/geo/alo/wms"
    }
}
